import { Capacitor } from "@capacitor/core";
import { isMobileWebBrowser } from "src/utils/platformUtils";
import { resolveUnsyncedPocIntegrationName } from "src/constants/unsyncedLeague";
import { getIsPollingRequest } from "src/utils/pollingContext";

interface ErrorResponse {
  detail?: string | { msg: string }[];
  message?: string;
}

export const getSuperAnalysisStream = async (
  authId: string,
  userDataPlatform: any,
  leagueIdPlatform: string,
  teamName: string,
  pocToken: string,
  accessAuthToken: string,
  signal?: AbortSignal,
  yahooToken?: string,
  forceRefresh?: boolean,
  calculatorSettings?: any,
  model?: "RotoGPT 5.0" | "RotoGPT FREE",
  /** Needed to safely map null → "un_synced" only for Un-Synced */
  platformId?: string | null,
): Promise<Response | string | undefined> => {
  try {
    const url = `${process.env.REACT_APP_BACKEND_URL}/functions/super_analysis_streaming`;

    const requestData: any = {
      league_integration: {
        pocToken: pocToken,
        leagueToken: userDataPlatform.integration_name === "yahoo" ? yahooToken : userDataPlatform.token,
        user_id: userDataPlatform.user_id,
        league_id: leagueIdPlatform,
        // Un-Synced: POC enum requires "un_synced" (userDataCache still stores null for news/rankings)
        integration_name: resolveUnsyncedPocIntegrationName(userDataPlatform.integration_name, platformId),
        force_refresh: forceRefresh,
      },
      sport: "NFL",
      response_settings: {
        persona_input: "standard",
        // max_words: 0,
        response_type: "markdown",
      },
      model: model || "RotoGPT 5.0",
      // model: "RotoGPT 5.0", //CAMBIAR AL DE ARRIBA
      // Add calculator_settings if provided (from LeagueSettings component)
      ...(calculatorSettings && { calculator_settings: calculatorSettings }),
    };

    // If the platform is ESPN, add the specific fields it requires
    if (userDataPlatform.integration_name === "espn") {
      requestData.league_integration.swid = userDataPlatform.swid;
      requestData.league_integration.espn_s2 = userDataPlatform.s2;
    }

    const options: RequestInit = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessAuthToken}`,
        Connection: "keep-alive",
        // Polling retries must not count toward the rate limit (RateLimitGuard skips this header)
        ...(getIsPollingRequest() && { "X-Polling-Request": "true" }),
      },
      body: JSON.stringify(requestData),
      signal,
      credentials: "include",
    };

    // TESTING RATE LIMIT DRAWER WITH 10 SECONDS THROWING ERROR
    // {
    //   const err = new Error("TOO_MANY_REQUESTS") as any;
    //   err.isRateLimit = true;
    //   err.retryAfter = 10; // 10 segundos para que veas el reloj en el drawer
    //   throw err;
    // }

    const response = await fetch(url, options);

    if (!response.ok) {
      // Detect rate limit before reading body
      if (response.status === 429) {
        const err = new Error("TOO_MANY_REQUESTS") as any;
        err.isRateLimit = true;
        const data429 = await response.json().catch(() => ({}));
        err.retryAfter = data429?.retryAfter ?? 60;
        throw err;
      }

      const errorData = await response.json();
      let errorMessage;

      if (Array.isArray(errorData.detail)) {
        errorMessage = errorData.detail[0].msg;
      } else {
        errorMessage = errorData.detail || errorData.message;
      }

      console.error("[getSuperAnalysisStream] Error response from backend:", errorData);
      throw new Error(errorMessage);
    }

    // Detect JSON (polling) vs Stream
    const contentType = response.headers.get("content-type");
    if (contentType?.includes("application/json")) {
      const jsonData = await response.json();
      if (jsonData.status === "INCOMPLETE") {
        return "INCOMPLETE";
      }
    }

    return response;
  } catch (error) {
    if (error.name === "AbortError") {
      throw error;
    }
    // Rate limit errors must never fall back to polling
    if ((error as any).isRateLimit) throw error;

    if (Capacitor.isNativePlatform() || isMobileWebBrowser() || error.name === "TypeError") {
      // iOS background freeze or transient network issue → handoff to polling
      console.warn(`[getSuperAnalysisStream] Stream error (${error.name}), falling back to polling`);
      return "INCOMPLETE";
    } else {
      console.error("Error in getSuperAnalysisStream API call:", error);
      throw error;
    }
  }
};
